import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/common/Toast';
import Navbar           from './components/common/Navbar';
import Footer           from './components/common/Footer';
import MerchantLogo     from './components/common/MerchantLogo';
import api              from './services/api';
import HomePage         from './pages/public/HomePage';
import CouponsPage      from './pages/public/CouponsPage';
import CouponDetailPage from './pages/public/CouponDetailPage';
import HowItWorksPage   from './pages/public/HowItWorksPage';
import AboutPage        from './pages/public/AboutPage';
import ForMerchantsPage from './pages/public/ForMerchantsPage';
import PricingPage      from './pages/public/PricingPage';
import ContactPage      from './pages/public/ContactPage';
import FAQPage          from './pages/public/FAQPage';
import { BlogPage, CareersPage, PressPage, PartnersPage, AffiliatePage } from './pages/public/MarketingPlaceholders';
import { ProfileLayout, WalletPage, RedemptionsPage, PayoutsPage, TierPage, ReferralsPage, AccountPage, SettingsPage } from './pages/public/ProfilePages';
import SubmitCouponPage from './pages/public/SubmitCouponPage';
import CommunityPage    from './pages/public/CommunityPage';
import UnsubscribePage  from './pages/public/UnsubscribePage';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, VerifyOtpPage } from './pages/auth/AuthPages';
import AdminDashboard   from './pages/admin/AdminDashboard';
import MerchantPortal   from './pages/merchant/MerchantPortal';
import ComingSoonLaunch from './pages/public/ComingSoonLaunch';
import { useMerchants } from './hooks/useMerchants';
import './styles.css';

/**
 * Launch gate: when VITE_COMING_SOON === 'true', every route renders the
 * full-screen Coming Soon page. Flip the env var to 'false' and rebuild
 * to reveal the real site.
 *
 * Backdoor: `?preview=1` on any URL persists a localStorage flag that
 * bypasses the gate for that browser. `?preview=0` clears it. Lets an
 * admin reach /login + /admin without rebuilding with the flag flipped.
 */
const COMING_SOON_MODE = import.meta.env.VITE_COMING_SOON === 'true';
const LAUNCH_PREVIEW_KEY = 'pv_launch_preview';

function isLaunchPreviewBypassed() {
  try {
    // Compliance bypass: the unsubscribe page must remain reachable even
    // while the public site is gated, otherwise marketing email footers
    // would break CAN-SPAM. Same logic for forgot/reset-password — those
    // links go out in transactional email and must work behind the gate.
    const path = window.location.pathname;
    if (path.startsWith('/newsletter/unsubscribe')) return true;
    if (path.startsWith('/forgot-password'))        return true;
    if (path.startsWith('/reset-password'))         return true;
    if (path.startsWith('/verify-otp'))             return true;

    const qs = new URLSearchParams(window.location.search);
    if (qs.has('preview')) {
      const v = qs.get('preview');
      if (v === '0' || v === 'false') {
        localStorage.removeItem(LAUNCH_PREVIEW_KEY);
        return false;
      }
      localStorage.setItem(LAUNCH_PREVIEW_KEY, '1');
      return true;
    }
    return localStorage.getItem(LAUNCH_PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
}

function ProtectedRoute({ children, requireAdmin, requireMerchant }) {
  const { user, isAdmin, isMerchant } = useAuth();
  if (!user) return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;

  if (requireAdmin && !isAdmin()) return <Navigate to="/" replace />;

  if (requireMerchant) {
    // Admins without an explicit merchant binding bounce to /admin rather
    // than seeing the merchant portal's "Almost ready / not linked to a
    // merchant" 403 page (which previously surfaced because the merchant
    // backend gates by users.merchant_id — and a super_admin almost never
    // has that set). Admins WHO ARE bound to a merchant fall through and
    // can use the portal normally.
    if (isAdmin() && !user?.merchant_id) return <Navigate to="/admin" replace />;
    if (!isMerchant() && !isAdmin())     return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Referral landing path: /r/:code → /register?ref=<code>. The register
 * controller (AuthController::register) reads ?ref to attach the referee.
 */
function ReferralRedirect() {
  const { code } = useParams();
  return <Navigate to={`/register?ref=${encodeURIComponent(code || '')}`} replace />;
}

function ScrollToTop() {
  // `search` matters as much as `pathname`: several footer links only change
  // the query (/coupons -> /coupons?featured=true). Watching pathname alone
  // meant clicking those from the footer left the viewport at the bottom, so
  // the new list rendered off-screen and the page looked unchanged.
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, search, hash]);
  return null;
}

function MerchantsPage() {
  const { merchants, loading } = useMerchants();
  return (
    <div>
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '72px 0 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', textAlign: 'center' }}>
          <span className="chip" style={{ marginBottom: 18 }}>🏪 Our brand partners</span>
          <h1 style={{ fontSize: 'clamp(32px, 4.4vw, 52px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 14 }}>
            <span className="gradient-text">500+</span> brands. One place to save.
          </h1>
          <p style={{ fontSize: 16, opacity: 0.85, maxWidth: 560, margin: '0 auto' }}>
            {!loading && `${merchants.length} verified merchants running active deals right now.`}
          </p>
        </div>
      </section>
      <div className="container" style={{ padding: '48px 20px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <div className="spinner" style={{ width: 36, height: 36 }} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {merchants.map(m => (
              <a key={m.id} href={m.website || '#'} target="_blank" rel="noopener noreferrer" className="cat-tile">
                <MerchantLogo merchant={m} size={56} style={{ margin: '0 auto 12px' }} />
                <p className="cat-name">{m.name}</p>
                <p className="cat-count">{m.category?.name || 'General'}</p>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StaticPage({ title, children }) {
  return (
    <div>
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '72px 0 48px', textAlign: 'center' }}>
        <div className="container-narrow">
          <h1 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 800, letterSpacing: '-0.025em' }}>{title}</h1>
        </div>
      </section>
      <div className="container-narrow" style={{ padding: '56px 20px 72px' }}>
        <div style={{ lineHeight: 1.8, color: '#4A5568', fontSize: 15 }}>{children}</div>
      </div>
    </div>
  );
}

function ImpersonationBanner() {
  const stash = (() => {
    try { return JSON.parse(localStorage.getItem('pv_admin_stash') || 'null'); } catch { return null; }
  })();
  if (!stash) return null;

  const stop = async () => {
    try {
      await api.post('/admin/impersonate/stop').catch(() => {}); // revoke even if banner is stale
    } finally {
      // Restore admin session no matter what (so the user is never stuck).
      if (stash.token) localStorage.setItem('pv_token', stash.token);
      else             localStorage.removeItem('pv_token');
      if (stash.user)  localStorage.setItem('pv_user', stash.user);
      else             localStorage.removeItem('pv_user');
      localStorage.removeItem('pv_admin_stash');
      window.location.assign('/admin');
    }
  };

  let userName = 'user';
  try { userName = JSON.parse(localStorage.getItem('pv_user') || '{}').name || 'user'; } catch {}

  return (
    <div style={{ background: 'linear-gradient(90deg,#FEF3C7,#FCD34D)', color: '#92400E', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, fontSize: 14, fontWeight: 600, position: 'sticky', top: 0, zIndex: 1100, borderBottom: '1px solid #F59E0B' }}>
      <span>👤 Impersonating <strong>{userName}</strong> — actions taken now are recorded against this user.</span>
      <button className="btn btn-secondary btn-sm" onClick={stop} style={{ background: '#92400E', color: '#fff', border: 'none' }}>Stop impersonating</button>
    </div>
  );
}

// Routes that render their own full-bleed brand experience (logo in the
// left auth header + a brand panel on the right). Showing the global
// Navbar + Footer on top of that puts the PerkVora wordmark on screen
// 3–4 times simultaneously; we hide both for these paths so the auth
// flow stays focused.
const AUTH_PATH_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-otp'];

function AppRoutes() {
  const { pathname } = useLocation();
  const isAuthPage = AUTH_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));

  return (
    <div className="page">
      <ScrollToTop />
      <ImpersonationBanner />
      {!isAuthPage && <Navbar />}
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/"              element={<HomePage />} />
          <Route path="/coupons"        element={<CouponsPage />} />
          <Route path="/coupons/:id"    element={<CouponDetailPage />} />
          <Route path="/submit-coupon"  element={<SubmitCouponPage />} />
          <Route path="/community"      element={<CommunityPage />} />
          <Route path="/merchants"      element={<MerchantsPage />} />

          {/* Marketing pages */}
          <Route path="/how-it-works"  element={<HowItWorksPage />} />
          <Route path="/about"         element={<AboutPage />} />
          <Route path="/for-merchants" element={<ForMerchantsPage />} />
          <Route path="/pricing"       element={<PricingPage />} />
          <Route path="/contact"       element={<ContactPage />} />
          <Route path="/faq"           element={<FAQPage />} />
          <Route path="/blog"          element={<BlogPage />} />
          <Route path="/blog/:slug"    element={<BlogPage />} />
          <Route path="/careers"       element={<CareersPage />} />
          <Route path="/press"         element={<PressPage />} />
          <Route path="/partners"      element={<PartnersPage />} />
          <Route path="/affiliate"     element={<AffiliatePage />} />

          {/* Auth */}
          <Route path="/login"            element={<LoginPage />} />
          <Route path="/register"         element={<RegisterPage />} />
          <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
          <Route path="/reset-password"   element={<ResetPasswordPage />} />
          <Route path="/verify-otp"       element={<VerifyOtpPage />} />
          <Route path="/r/:code"          element={<ReferralRedirect />} />

          {/* Profile (auth-gated) */}
          <Route path="/profile" element={<ProtectedRoute><ProfileLayout /></ProtectedRoute>}>
            <Route index               element={<AccountPage />} />
            <Route path="wallet"       element={<WalletPage />} />
            <Route path="redemptions"  element={<RedemptionsPage />} />
            <Route path="payouts"      element={<PayoutsPage />} />
            <Route path="tier"         element={<TierPage />} />
            <Route path="referrals"    element={<ReferralsPage />} />
            <Route path="settings"     element={<SettingsPage />} />
          </Route>

          {/* Portals */}
          <Route path="/admin/*"    element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          <Route path="/merchant/*" element={<ProtectedRoute requireMerchant><MerchantPortal /></ProtectedRoute>} />

          {/* Newsletter unsubscribe (linked from email footers) */}
          <Route path="/newsletter/unsubscribe" element={<UnsubscribePage />} />

          {/* Legal */}
          <Route path="/disclosure" element={<StaticPage title="Affiliate Disclosure"><p>PerkVora participates in affiliate marketing programs. When you click certain links, we may earn a small commission from the merchant — at no extra cost to you. This income helps us keep PerkVora free for everyone.</p><p style={{marginTop:16}}>Questions? Email <a href="mailto:hello@perkvora.com">hello@perkvora.com</a></p></StaticPage>} />
          <Route path="/privacy"    element={<StaticPage title="Privacy Policy"><p>PerkVora is committed to protecting your privacy. We collect your name, email, and location to personalize your deal feed. We do not sell your personal information.</p><p style={{marginTop:16}}><strong>California residents:</strong> You may opt out of data sale under CCPA. Email <a href="mailto:privacy@perkvora.com">privacy@perkvora.com</a> to exercise your rights.</p></StaticPage>} />
          <Route path="/terms"      element={<StaticPage title="Terms of Service"><p>By using PerkVora, you agree that coupons are for personal use only. Resale of coupon codes is prohibited. PerkVora does not guarantee merchant acceptance of any coupon.</p></StaticPage>} />
          <Route path="/ccpa"       element={<StaticPage title="California Privacy Rights"><p>California residents may request access to, deletion of, or opt-out from sale of personal information under CCPA. Email <a href="mailto:privacy@perkvora.com">privacy@perkvora.com</a>.</p></StaticPage>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isAuthPage && <Footer />}
    </div>
  );
}

export default function App() {
  if (COMING_SOON_MODE && !isLaunchPreviewBypassed()) {
    return <ComingSoonLaunch />;
  }
  return (
    <BrowserRouter>
      <AppProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </AppProvider>
    </BrowserRouter>
  );
}
